import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { SearchBar } from '@/components/home/search-bar';
import { KeywordFilters } from '@/components/home/keyword-filters';
import { Heart, HelpCircle, Newspaper } from 'lucide-react';
import { useFavorites } from '@/lib/hooks/use-favorites';
import { cn, normalizeImageUrl } from '@/lib/utils';
import Loading from '@/components/loading';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useContentList } from '@/lib/hooks/use-content';
import { canSeeBinauralBeats, getDeepLink, IconMap } from '@/lib/constants';
import { Content, ContentType, ContentWithConfig } from '@/types/custom';
import { searchContentList } from '@/lib/search';
import AudioCard from '@/components/audio-card';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';
import { AdminRole } from '@/types/custom';
import { Database } from '@/types/supabase';

type NewsItem = Database['public']['Tables']['news_items']['Row'];
type NewsItemWithKeywords = NewsItem & { keywords: { id: string; name: string }[] };

export default function SearchPage() {
  const { contentWithConfigs, loading: loadingContent } = useContentList('*');
  const navigate = useNavigate();
  const { profile } = useProfile();
  const showBinaural = canSeeBinauralBeats(profile?.role === AdminRole);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchQuery = queryParams.get('query');

  const [results, setResults] = useState<ContentWithConfig[]>([]);
  const [newsResults, setNewsResults] = useState<NewsItemWithKeywords[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingNews, setLoadingNews] = useState(true);
  const { favorites, toggleFavorite } = useFavorites();

  // Fetch news items with keywords
  useEffect(() => {
    const fetchNews = async () => {
      setLoadingNews(true);
      const { data } = await supabase
        .from('news_items')
        .select('*, news_item_keywords(keyword_id, keywords(id, name))')
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (data) {
        const items: NewsItemWithKeywords[] = data.map((item: any) => ({
          ...item,
          keywords: (item.news_item_keywords ?? []).map((nik: any) => nik.keywords).filter(Boolean),
        }));

        if (searchQuery?.trim()) {
          const query = searchQuery.toLowerCase().trim();
          const filtered = items.filter((item) => {
            if (item.title.toLowerCase().includes(query)) return true;
            if (item.body?.toLowerCase().includes(query)) return true;
            if (item.keywords.some((k) => k.name.toLowerCase() === query)) return true;
            if (item.keywords.some((k) => k.name.toLowerCase().split(/[\s-]+/).some((w) => w.includes(query)))) return true;
            return false;
          });
          setNewsResults(filtered);
        } else {
          setNewsResults(items);
        }
      }
      setLoadingNews(false);
    };
    fetchNews();
  }, [searchQuery]);

  useEffect(() => {
    if (loadingContent) {
      setResults([]);
      return;
    }
    if (!searchQuery?.trim()) {
      setResults(contentWithConfigs);
      setLoading(false);
      return;
    }

    const results = searchContentList(searchQuery, contentWithConfigs);
    const contentResults = contentWithConfigs.filter((_, idx) => results[idx]);
    setResults(contentResults);
    setLoading(false);
  }, [searchQuery, contentWithConfigs, loadingContent]);

  const filterByContentType = (
    contentType: ContentType,
  ): ContentWithConfig[] => {
    return results.filter((item) => item.content.content_type === contentType);
  };

  // Group results by content type
  const meditationResults = [
    ...filterByContentType('meditation'),
    ...filterByContentType('journey'),
  ];
  const musicResults = filterByContentType('music');
  const natureResults = filterByContentType('nature_sound');
  const binauralResults = filterByContentType('binaural');
  const articleResults = filterByContentType('article');

  const getDefaultTab = () => {
    return 'meditaties';
  };

  const renderContentCards = (items: Content[]) => {
    if (items.length === 0) {
      return <p className="text-muted-foreground">Geen resultaten gevonden.</p>;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {items.map((item) => {
          return (
            <AudioCard
              key={item.id}
              content={item}
              isFavorite={favorites[item.id]}
              onClick={() => {
                navigate(getDeepLink(item.content_type, item.id));
              }}
              onFavoriteToggle={(e) => {
                e.stopPropagation();
                toggleFavorite(item.id);
              }}
            />
          );
        })}
      </div>
    );
  };

  const renderNewsCards = (items: NewsItemWithKeywords[]) => {
    if (items.length === 0) {
      return <p className="text-muted-foreground">Geen resultaten gevonden.</p>;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {items.map((item) => (
          <Card
            key={item.id}
            className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(`/nieuws`)}
          >
            {item.image_url && (
              <img
                src={normalizeImageUrl(item.image_url)}
                alt={item.title}
                className="w-full h-40 object-cover"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <div className="p-4">
              <h4 className="font-medium text-sm line-clamp-2">{item.title}</h4>
              {item.body && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.body.replace(/<[^>]*>/g, '')}</p>
              )}
            </div>
          </Card>
        ))}
      </div>
    );
  };

  const isLoading = loading || loadingNews;
  const totalResults = results.length + newsResults.length;

  return (
    <div className="min-h-screen p-4 sm:p-8 space-y-6 sm:space-y-8">
      <div className="mb-8">
        <div className="max-w-xl mb-4">
          <SearchBar />
        </div>
        <KeywordFilters />
      </div>

      <div className="space-y-4">
        <h2 className="text-2xl font-semibold">
          {searchQuery
            ? `Zoekresultaten voor "${searchQuery}"`
            : 'Alle content'}
        </h2>
        {isLoading ? (
          <Loading />
        ) : totalResults === 0 ? (
          <p>Geen resultaten gevonden.</p>
        ) : (
          <Tabs defaultValue={getDefaultTab()} className="w-full">
            <TabsList className="w-full grid grid-cols-3 gap-2 sm:flex sm:justify-start sm:gap-0 border-b sm:border-b rounded-none p-0 h-auto">
              <TabsTrigger
                value="meditaties"
                className="flex items-center justify-center sm:justify-start gap-2 data-[state=active]:bg-green-100 sm:data-[state=active]:bg-transparent data-[state=active]:border-b-2 border-green-500 rounded-md sm:rounded-none px-4 py-2"
              >
                <IconMap.meditation className="h-4 w-4" />
                <span className="hidden sm:inline">Meditaties</span>
                <span className="sm:hidden">Meditaties</span>
                <span className="hidden sm:inline">({meditationResults.length})</span>
              </TabsTrigger>
              <TabsTrigger
                value="muziek"
                className="flex items-center justify-center sm:justify-start gap-2 data-[state=active]:bg-green-100 sm:data-[state=active]:bg-transparent data-[state=active]:border-b-2 border-green-500 rounded-md sm:rounded-none px-4 py-2"
              >
                <IconMap.music className="h-4 w-4" />
                <span className="hidden sm:inline">Muziek / Frequenties ({musicResults.length})</span>
                <span className="sm:hidden">Muziek</span>
              </TabsTrigger>
              <TabsTrigger
                value="natuurgeluiden"
                className="flex items-center justify-center sm:justify-start gap-2 data-[state=active]:bg-green-100 sm:data-[state=active]:bg-transparent data-[state=active]:border-b-2 border-green-500 rounded-md sm:rounded-none px-4 py-2"
              >
                <IconMap.nature_sound className="h-4 w-4" />
                <span className="hidden sm:inline">Natuurgeluiden ({natureResults.length})</span>
                <span className="sm:hidden">Natuur</span>
              </TabsTrigger>
              {showBinaural && (
                <TabsTrigger
                  value="binaural"
                  className="flex items-center justify-center sm:justify-start gap-2 data-[state=active]:bg-green-100 sm:data-[state=active]:bg-transparent data-[state=active]:border-b-2 border-green-500 rounded-md sm:rounded-none px-4 py-2"
                >
                  <IconMap.binaural className="h-4 w-4" />
                  <span className="hidden sm:inline">Binaural Beats ({binauralResults.length})</span>
                  <span className="sm:hidden">Binaural</span>
                </TabsTrigger>
              )}
              <TabsTrigger
                value="artikelen"
                className="flex items-center justify-center sm:justify-start gap-2 data-[state=active]:bg-green-100 sm:data-[state=active]:bg-transparent data-[state=active]:border-b-2 border-green-500 rounded-md sm:rounded-none px-4 py-2"
              >
                <HelpCircle className="h-4 w-4" />
                <span className="hidden sm:inline">Tips & Artikelen ({articleResults.length})</span>
                <span className="sm:hidden">Artikelen</span>
              </TabsTrigger>
              <TabsTrigger
                value="nieuws"
                className="flex items-center justify-center sm:justify-start gap-2 data-[state=active]:bg-green-100 sm:data-[state=active]:bg-transparent data-[state=active]:border-b-2 border-green-500 rounded-md sm:rounded-none px-4 py-2"
              >
                <Newspaper className="h-4 w-4" />
                <span className="hidden sm:inline">Nieuws ({newsResults.length})</span>
                <span className="sm:hidden">Nieuws</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="meditaties" className="mt-6">
              <h3 className="text-xl font-semibold mb-4">Meditaties</h3>
              {renderContentCards(meditationResults.map((x) => x.content))}
            </TabsContent>

            <TabsContent value="muziek" className="mt-6">
              <h3 className="text-xl font-semibold mb-4">
                Muziek / Frequenties
              </h3>
              {renderContentCards(musicResults.map((x) => x.content))}
            </TabsContent>

            <TabsContent value="natuurgeluiden" className="mt-6">
              <h3 className="text-xl font-semibold mb-4">Natuurgeluiden</h3>
              {renderContentCards(natureResults.map((x) => x.content))}
            </TabsContent>

            <TabsContent value="binaural" className="mt-6">
              <h3 className="text-xl font-semibold mb-4">Binaural Beats</h3>
              {renderContentCards(binauralResults.map((x) => x.content))}
            </TabsContent>

            <TabsContent value="artikelen" className="mt-6">
              <h3 className="text-xl font-semibold mb-4">Tips & Artikelen</h3>
              {renderContentCards(articleResults.map((x) => x.content))}
            </TabsContent>

            <TabsContent value="nieuws" className="mt-6">
              <h3 className="text-xl font-semibold mb-4">Nieuws</h3>
              {renderNewsCards(newsResults)}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
